import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const UserWiseCallCenterSurveyDash = () => {
    // --- State ---
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    
    // Empty by default as requested
    const [selectedDate, setSelectedDate] = useState("");

    // --- Hooks & Context ---
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "User Wise Survey",
            subtitle: "Call center survey statistics per user",
            filterPlaceholder: "Search by name or mobile...",
            selectDate: "Select Date:",
            selectDatePlaceholder: "DD/MM/YYYY",
            loadingData: "Loading Data...",
            noData: "No survey data found.",
            totalCall: "Total Call",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say",
            notReceived: "Not Received",
            wrongMobile: "Wrong Mobile"
        },
        hi: {
            pageTitle: "उपयोगकर्ता वार सर्वेक्षण",
            subtitle: "प्रत्येक उपयोगकर्ता के लिए कॉल सेंटर सर्वेक्षण आंकड़े",
            filterPlaceholder: "नाम या मोबाइल द्वारा खोजें...",
            selectDate: "तिथि चुनें:",
            selectDatePlaceholder: "DD/MM/YYYY",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई सर्वेक्षण डेटा नहीं मिला।",
            totalCall: "कुल कॉल",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "कह नहीं सकते",
            notReceived: "प्राप्त नहीं",
            wrongMobile: "गलत मोबाइल"
        },
        gu: {
            pageTitle: "વપરાશકર્તા વાઈઝ સર્વે",
            subtitle: "દરેક વપરાશકર્તા માટે કૉલ સેન્ટર સર્વે આંકડા",
            filterPlaceholder: "નામ અથવા મોબાઇલથી શોધો...",
            selectDate: "તારીખ પસંદ કરો:",
            selectDatePlaceholder: "DD/MM/YYYY",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ સર્વેક્ષણ ડેટા મળ્યો નથી.",
            totalCall: "કુલ કૉલ",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            cantSay: "કહી શકતો નથી",
            notReceived: "પ્રાપ્ત થયું નથી",
            wrongMobile: "ખોટો મોબાઈલ"
        }
    };

    const txt = t[lang];

    // Helper function to convert "2026-03-30" to "30,MAR,2026" for the API
    const formatDateForApi = (yyyyMmDd) => {
        if (!yyyyMmDd) return "";
        const [year, month, day] = yyyyMmDd.split('-');
        const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
        const monthName = monthNames[parseInt(month, 10) - 1];
        return `${day},${monthName},${year}`;
    };

    // --- API Fetch Logic ---
    const fetchUserData = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const payloadDate = formatDateForApi(selectedDate);
            
            const response = await axios.post(
                `${BASE_URL}/api/dis_user_wise_call_center_survey_dash`,
                { 
                    app_id: appId,
                    date: payloadDate
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setData(response.data.data.result);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error fetching user wise survey data:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // Re-fetch whenever the selected date changes
    useEffect(() => {
        fetchUserData();
    }, [appId, selectedDate]);

    // --- Filter Logic ---
    const filteredData = data.filter((item) => {
        if (!searchTerm) return true;
        const lowerSearch = searchTerm.toLowerCase();
        return (
            (item.name && item.name.toLowerCase().includes(lowerSearch)) ||
            (item.mobile_no && item.mobile_no.includes(lowerSearch))
        );
    });

    // --- Loading State ---
    if (loading && data.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #f8fafc; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header
                title={txt.pageTitle}
            />

            {/* Modern Search & Date Filter Row */}
            <div className="filter-container mt-3 mb-4">
                <div className="search-wrapper-modern-container flex-grow-1" style={{ maxWidth: '400px' }}>
                    <div className="search-wrapper-modern w-100">
                        <svg className="search-icon-modern" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            className="search-input-modern"
                            type="text"
                            placeholder={txt.filterPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="date-picker-wrapper" style={{height:"47px"}}>
                    <label className="date-picker-label d-none d-md-block">{txt.selectDate}</label>
                    <input
                        type="date"
                        className="date-input-modern"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        placeholder={txt.selectDatePlaceholder}
                    />
                </div>
            </div>

            {/* Loading Overlay for Background Fetching */}
            {loading && data.length > 0 && (
                <div className="text-center text-primary mb-3">
                    <small>{txt.loadingData}</small>
                </div>
            )}

            {/* No Data State */}
            {filteredData.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">👥</div>
                        <p>{txt.noData} {searchTerm && `"${searchTerm}"`}</p>
                    </div>
                </div>
            )}

            {/* --- DATA GRID --- */}
            <div className="dashboard-grid pb-5">
                {filteredData.map((item, index) => (
                    <div 
                        className="survey-stat-card" 
                        key={index}
                        onClick={() => navigate('/dis_user_wise_call_center_survey_voter', { state: { user_id: item.user_id } })}
                        style={{ cursor: 'pointer' }}
                    >
                        
                        {/* Card Header: Avatar & Name */}
                        <div className="card-top-row">
                            <div className="card-middle">
                                <h3 className="user-name">{item.name}</h3>
                                <div className="user-mobile-wrap">
                                    <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24" className="phone-icon me-1">
                                        <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                    </svg>
                                    <span className="user-mobile">{item.mobile_no}</span>
                                </div>
                            </div>
                        </div>

                        {/* Card Body: Stats Grid */}
                        <div className="card-body-row">
                            <div className="stats-wrapper w-100">
                                
                                {/* Top Row: 3 Items */}
                                <div className="stats-row-top">
                                    <div className="m-stat-box">
                                        <span className="m-val c-total">{item.total}</span>
                                        <span className="m-lbl">{txt.totalCall}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-nr">{item.nr}</span>
                                        <span className="m-lbl">{txt.notReceived}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-wm">{item.wm}</span>
                                        <span className="m-lbl">{txt.wrongMobile}</span>
                                    </div>
                                </div>

                                {/* Bottom Row: 4 Items */}
                                <div className="stats-row-bottom">
                                    <div className="m-stat-box">
                                        <span className="m-val c-p">{item.p}</span>
                                        <span className="m-lbl">{txt.positive}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-n">{item.n}</span>
                                        <span className="m-lbl">{txt.negative}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-d">{item.d}</span>
                                        <span className="m-lbl">{txt.doubtful}</span>
                                    </div>
                                    <div className="m-stat-box">
                                        <span className="m-val c-c">{item.c}</span>
                                        <span className="m-lbl">{txt.cantSay}</span>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                /* --- SEARCH & FILTER STYLE --- */
                .filter-container {
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    gap: 16px;
                }
                .search-wrapper-modern { 
                    position: relative; 
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .search-icon-modern { 
                    position: absolute; left: 14px; top: 50%; transform: translateY(-50%); 
                    width: 20px; height: 20px; color: #94a3b8; pointer-events: none; 
                }
                .search-input-modern { 
                    width: 100%; padding: 12px 16px 12px 42px; border: none; border-radius: 12px; 
                    font-size: 14px; color: #1e293b; background-color: transparent; outline: none; 
                    transition: all 0.2s ease; 
                }
                .search-wrapper-modern:focus-within { 
                    border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); 
                }
                
                /* Date Picker - Styled Popup */
                .date-picker-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: white;
                    padding: 8px 16px;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                }
                .date-picker-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #475569;
                    margin: 0;
                }
                .date-input-modern {
                    border: none;
                    background: transparent;
                    font-size: 15px;
                    font-weight: 700;
                    color: #1e293b;
                    outline: none;
                    cursor: pointer;
                }
                
                /* Style for the date picker popup calendar */
                .date-input-modern::-webkit-calendar-picker-indicator {
                    cursor: pointer;
                    filter: invert(0.4) sepia(1) saturate(5) hue-rotate(180deg);
                    padding: 5px;
                    border-radius: 4px;
                    transition: all 0.2s ease;
                }
                
                .date-input-modern::-webkit-calendar-picker-indicator:hover {
                    background-color: #f1f5f9;
                    transform: scale(1.1);
                }
                
                /* Styling the date picker popup (webkit browsers) */
                ::-webkit-datetime-edit {
                    padding: 0 4px;
                }
                
                ::-webkit-datetime-edit-year-field,
                ::-webkit-datetime-edit-month-field,
                ::-webkit-datetime-edit-day-field {
                    color: #1e293b;
                    font-weight: 500;
                }
                
                /* When date is empty, show placeholder styling */
                .date-input-modern:invalid::-webkit-datetime-edit {
                    color: #94a3b8;
                }
                
                .date-input-modern:invalid {
                    color: #94a3b8;
                }
                
                /* Style for the date picker popup (global) */
                :global(::-webkit-calendar-picker) {
                    background: white;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
                }
                
                /* Custom styling for the date picker popup */
                :global(input[type="date"]::-webkit-calendar-picker-indicator:focus) {
                    outline: none;
                }
                
                :global(input[type="date"]:focus) {
                    border-color: #2563eb;
                }

                .result-count {
                    font-size: 14px; font-weight: 600; color: #64748b; margin-left: auto;
                }
                .count-badge {
                    background: #e2e8f0; color: #1e293b; padding: 4px 10px; border-radius: 12px;
                }

                /* --- DASHBOARD GRID --- */
                .dashboard-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
                    gap: 24px;
                }

                /* --- CARD STYLES --- */
                .survey-stat-card {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 20px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
                    border: 1px solid #f1f5f9;
                    transition: all 0.3s ease;
                    display: flex;
                    flex-direction: column;
                }
                .survey-stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.08);
                    border-color: #e2e8f0;
                }

                /* Card Top Row: User Info */
                .card-top-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #f1f5f9;
                    margin-bottom: 16px;
                }
                .avatar-box {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    overflow: hidden;
                    background: #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    border: 2px solid #f8fafc;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                }
                .avatar-box img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .avatar-placeholder {
                    font-size: 20px;
                    font-weight: 700;
                    color: #64748b;
                }
                .card-middle {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .user-name {
                    font-size: 1.1rem;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0;
                }
                .user-mobile-wrap {
                    display: flex;
                    align-items: center;
                    color: #64748b;
                }
                .user-mobile {
                    font-size: 0.9rem;
                    font-weight: 500;
                    font-family: monospace;
                    letter-spacing: 0.5px;
                }
                .phone-icon {
                    color: #3b82f6;
                }

                /* --- ROW-BASED STATS GRID --- */
                .card-body-row {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }
                .stats-wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .stats-row-top {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 10px;
                }
                .stats-row-bottom {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 10px;
                }
                .m-stat-box {
                    background: #f8fafc;
                    border-radius: 8px;
                    padding: 10px 4px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    border: 1px solid #f1f5f9;
                }
                .m-val {
                    font-size: 18px;
                    font-weight: 800;
                    line-height: 1.2;
                }
                .m-lbl {
                    font-size: 10px;
                    font-weight: 700;
                    color: #64748b;
                    margin-top: 4px;
                    line-height: 1.1;
                }

                /* Parameter Colors */
                .c-total { color: #3b82f6; }
                .c-p { color: #10b981; }
                .c-n { color: #ef4444; }
                .c-d { color: #f59e0b; }
                .c-c { color: #6366f1; }
                .c-nr { color: #94a3b8; }
                .c-wm { color: #cbd5e1; }

                .empty-state { background: white; border-radius: 20px; border: 2px dashed #e2e8f0; }
                .empty-icon { font-size: 3rem; margin-bottom: 10px; opacity: 0.5; }

                /* ========== ONLY RESPONSIVE CHANGES ADDED BELOW ========== */
                @media (max-width: 1024px) {
                    .dashboard-grid {
                        grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
                        gap: 20px;
                    }
                }

                @media (max-width: 768px) {
                    .filter-container {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 12px;
                    }
                    
                    .search-wrapper-modern-container {
                        max-width: 100% !important;
                    }
                    
                    .date-picker-wrapper {
                        justify-content: space-between;
                        width: 100%;
                    }
                    
                    .date-picker-label {
                        display: inline-block !important;
                    }
                    
                    .result-count {
                        margin-left: 0;
                        text-align: center;
                        display: inline-block !important;
                    }
                    
                    .dashboard-grid {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }
                    
                    .stats-row-bottom {
                        grid-template-columns: repeat(2, 1fr);
                    }
                    
                    .card-top-row {
                        gap: 12px;
                    }
                    
                    .user-name {
                        font-size: 1rem;
                    }
                    
                    .user-mobile {
                        font-size: 0.85rem;
                    }
                    
                    .date-input-modern {
                        font-size: 14px;
                    }
                }

                @media (max-width: 480px) {
                    .dashboard-grid {
                        gap: 12px;
                    }
                    
                    .survey-stat-card {
                        padding: 16px;
                    }
                    
                    .stats-row-top,
                    .stats-row-bottom {
                        gap: 6px;
                    }
                    
                    .m-stat-box {
                        padding: 6px 2px;
                    }
                    
                    .m-val {
                        font-size: 16px;
                    }
                    
                    .m-lbl {
                        font-size: 8px;
                    }
                    
                    .user-name {
                        font-size: 0.9rem;
                    }
                    
                    .user-mobile {
                        font-size: 0.75rem;
                    }
                    
                    .date-picker-wrapper {
                        padding: 6px 12px;
                    }
                    
                    .date-input-modern {
                        font-size: 13px;
                    }
                }
            `}</style>
        </div>
    );
};

export default UserWiseCallCenterSurveyDash;